/**
 * Email design defaults.
 *
 * Each entry is a distinct visual layout that operators pick as a starting
 * point in the create wizard. The visual editor then opens with the design
 * already populated — copy is intentionally generic so operators replace it
 * with their own.
 *
 * Each design ships with a `text` field (HTML body for the editor's canvas)
 * and an optional `style` field (CSS rules the canvas applies). Inline styles
 * are used heavily for email-client compatibility.
 */

export interface EmailTemplateDefault {
  key: string;
  category: string;
  name: string;
  description: string;
  subject: string | null;
  text: string;
  style?: string;
}

const FONT_SANS = `'Inter','Helvetica Neue',Helvetica,Arial,sans-serif`;
const FONT_SERIF = `Georgia,'Times New Roman',serif`;

const pageStyle = (bg: string) =>
  `[data-gjs-type="wrapper"] { background: ${bg}; min-height: 100vh; } body { margin: 0; background: ${bg}; }`;

// ---------- 1. Plain Letter ----------
const plainLetter: EmailTemplateDefault = {
  key: "design.plain-letter",
  style: pageStyle("#ffffff"),
  category: "Minimal",
  name: "Plain Letter",
  description:
    "Pure typography. No banners, no boxes. Feels like a personal note.",
  subject: "A quick note for you",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#ffffff;">
  <tr><td align="center" style="padding:48px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="540" style="max-width:540px;width:100%;font-family:${FONT_SANS};color:#2F3847;">
      <tr><td style="font-size:16px;line-height:1.7;">
        <p style="margin:0 0 20px 0;">Hi {{ firstName }},</p>
        <p style="margin:0 0 20px 0;">Open with the reason you're writing, one sentence, plainly stated. The whole tone of this design is a note from a person, not a brand, so the typography stays out of the way.</p>
        <p style="margin:0 0 20px 0;">Use the middle for anything that needs to be said: a recommendation, a piece of news, a follow-up. Two short paragraphs do more work here than ten formatted ones.</p>
        <p style="margin:0 0 32px 0;">Close with whatever the next step is. Replying is easy and direct.</p>
        <p style="margin:0 0 4px 0;">{{ agentName }}</p>
        <p style="margin:0;color:#2F3847;font-size:14px;">{{ companyName }} · {{ agentPhone }}</p>
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 2. Blue Banner ----------
const indigoBanner: EmailTemplateDefault = {
  key: "design.indigo-banner",
  style: pageStyle("#E4E9ED"),
  category: "Banner",
  name: "Blue Banner",
  description:
    "Colored title bar over a clean white body with a matching CTA button.",
  subject: "Your headline goes right here",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#E4E9ED;">
  <tr><td align="center" style="padding:32px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;width:100%;background:#ffffff;font-family:${FONT_SANS};">
      <tr><td style="background:#0033A0;padding:28px 32px;">
        <div style="color:#ffffff;font-size:13px;letter-spacing:0.08em;text-transform:uppercase;font-weight:600;opacity:0.85;">{{ companyName }}</div>
        <div style="color:#ffffff;font-size:24px;font-weight:700;line-height:1.3;margin-top:6px;">Your headline goes right here</div>
      </td></tr>
      <tr><td style="padding:32px;color:#2F3847;font-size:15px;line-height:1.65;">
        <p style="margin:0 0 16px 0;">Hi {{ firstName }},</p>
        <p style="margin:0 0 16px 0;">Open with the context: why you're writing and what's in it for the reader. Two or three sentences is the sweet spot.</p>
        <p style="margin:0 0 28px 0;">Lead them naturally toward the action below.</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0">
          <tr><td style="background:#0033A0;border-radius:6px;">
            <a href="#" style="display:inline-block;padding:12px 24px;color:#ffffff;font-size:15px;font-weight:600;text-decoration:none;">Take the next step</a>
          </td></tr>
        </table>
      </td></tr>
      <tr><td style="padding:24px 32px;border-top:1px solid #CED7E0;color:#2F3847;font-size:12px;line-height:1.5;">
        {{ companyName }} · {{ agentPhone }} · <a href="mailto:{{ agentEmail }}" style="color:#0033A0;text-decoration:none;">{{ agentEmail }}</a>
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 3. Centered Card ----------
const centeredCard: EmailTemplateDefault = {
  key: "design.centered-card",
  style: pageStyle("#E4E9ED"),
  category: "Card",
  name: "Centered Card",
  description:
    "Single rounded card floating on a tinted background. Calm and focused.",
  subject: "Something for your attention",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#E4E9ED;">
  <tr><td align="center" style="padding:56px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="520" style="max-width:520px;width:100%;background:#ffffff;border-radius:14px;font-family:${FONT_SANS};">
      <tr><td style="padding:48px 44px;color:#2F3847;">
        <div style="font-size:11px;letter-spacing:0.16em;text-transform:uppercase;color:#0033A0;font-weight:700;">{{ companyName }}</div>
        <h1 style="margin:14px 0 18px 0;font-size:26px;line-height:1.25;font-weight:700;color:#0E1941;">A short, deliberate headline.</h1>
        <p style="margin:0 0 18px 0;font-size:15px;line-height:1.65;color:#2F3847;">Hi {{ firstName }}, this is the kind of message you read once and act on. The card-on-background design gives every line room to breathe.</p>
        <p style="margin:0 0 28px 0;font-size:15px;line-height:1.65;color:#2F3847;">Drop in two short paragraphs of context and then move to the action.</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0">
          <tr><td style="background:#0E1941;border-radius:8px;">
            <a href="#" style="display:inline-block;padding:13px 28px;color:#ffffff;font-size:14px;font-weight:600;text-decoration:none;letter-spacing:0.02em;">Continue</a>
          </td></tr>
        </table>
      </td></tr>
    </table>
    <div style="margin-top:18px;color:#2F3847;font-size:12px;font-family:${FONT_SANS};">
      {{ companyName }} · {{ agentPhone }}
    </div>
  </td></tr>
</table>`.trim(),
};

// ---------- 4. Bold Hero ----------
const boldHero: EmailTemplateDefault = {
  key: "design.bold-hero",
  style: pageStyle("#ffffff"),
  category: "Hero",
  name: "Bold Hero",
  description:
    "Oversized headline on dark, breathable body underneath. Reads like a launch.",
  subject: "A statement worth opening for",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#ffffff;">
  <tr><td align="center">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;width:100%;font-family:${FONT_SANS};">
      <tr><td style="background:#0E1941;padding:72px 40px 64px 40px;">
        <div style="color:#45BCE5;font-size:12px;letter-spacing:0.18em;text-transform:uppercase;font-weight:600;">{{ companyName }}</div>
        <h1 style="margin:18px 0 0 0;color:#ffffff;font-size:42px;line-height:1.1;font-weight:800;letter-spacing:-0.02em;">A bold statement,<br/>made simply.</h1>
      </td></tr>
      <tr><td style="padding:40px;color:#2F3847;font-size:16px;line-height:1.65;">
        <p style="margin:0 0 18px 0;">Hi {{ firstName }},</p>
        <p style="margin:0 0 18px 0;">Use the body to back up the headline: what the news is, why it matters, what it means for them. Keep paragraphs short so the hero doesn't carry the whole email by itself.</p>
        <p style="margin:0 0 32px 0;">Then hand them a clean next step.</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0">
          <tr><td style="background:#0E1941;border-radius:4px;">
            <a href="#" style="display:inline-block;padding:14px 30px;color:#ffffff;font-size:15px;font-weight:600;text-decoration:none;">Learn more</a>
          </td></tr>
        </table>
      </td></tr>
      <tr><td style="padding:24px 40px 36px 40px;color:#2F3847;font-size:12px;line-height:1.5;">
        {{ agentName }}, {{ companyName }}
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 5. Image Hero ----------
const imageHero: EmailTemplateDefault = {
  key: "design.image-hero",
  style: pageStyle("#E4E9ED"),
  category: "Hero",
  name: "Image Hero",
  description:
    "Full-width image at the top, body and CTA underneath. Drop your photo in.",
  subject: "An image-first announcement",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#E4E9ED;">
  <tr><td align="center" style="padding:24px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;width:100%;background:#ffffff;font-family:${FONT_SANS};">
      <tr><td style="height:260px;background:linear-gradient(135deg,#45BCE5 0%,#0033A0 100%);background-color:#45BCE5;text-align:center;vertical-align:middle;color:#0033A0;font-size:14px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;">
        Replace with your image
      </td></tr>
      <tr><td style="padding:36px 40px 12px 40px;">
        <h1 style="margin:0;font-size:28px;line-height:1.25;font-weight:700;color:#0E1941;">A clear headline that pairs with your photo.</h1>
      </td></tr>
      <tr><td style="padding:8px 40px 32px 40px;color:#2F3847;font-size:15px;line-height:1.65;">
        <p style="margin:0 0 16px 0;">Hi {{ firstName }},</p>
        <p style="margin:0 0 28px 0;">Use this space to expand on the visual: what's in the picture, why it matters, and what you want them to do next. Two short paragraphs is plenty.</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0">
          <tr><td style="background:#0033A0;border-radius:6px;">
            <a href="#" style="display:inline-block;padding:12px 26px;color:#ffffff;font-size:15px;font-weight:600;text-decoration:none;">View details</a>
          </td></tr>
        </table>
      </td></tr>
      <tr><td style="padding:20px 40px 32px 40px;border-top:1px solid #CED7E0;color:#2F3847;font-size:12px;">
        {{ companyName }} · {{ agentPhone }}
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 6. Two-Column Feature ----------
const twoColumnFeature: EmailTemplateDefault = {
  key: "design.two-column",
  style: pageStyle("#ffffff"),
  category: "Hero",
  name: "Two-Column Feature",
  description: "Image on the left, text on the right. Magazine spread feel.",
  subject: "A side-by-side feature",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#ffffff;">
  <tr><td align="center" style="padding:32px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;width:100%;font-family:${FONT_SANS};">
      <tr><td style="padding-bottom:24px;color:#2F3847;font-size:11px;letter-spacing:0.18em;text-transform:uppercase;font-weight:700;">
        {{ companyName }}
      </td></tr>
      <tr><td>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>
            <td width="260" valign="top" style="background:#E4E9ED;height:260px;text-align:center;vertical-align:middle;color:#0033A0;font-size:12px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;">
              Your image here
            </td>
            <td width="20"></td>
            <td valign="top">
              <h1 style="margin:0 0 14px 0;font-size:22px;line-height:1.3;font-weight:700;color:#0E1941;">A headline that earns the photo next to it.</h1>
              <p style="margin:0 0 14px 0;font-size:14px;line-height:1.6;color:#2F3847;">Hi {{ firstName }}, drop in a short paragraph of context about whatever is on the left.</p>
              <p style="margin:0 0 22px 0;font-size:14px;line-height:1.6;color:#2F3847;">Keep it tight so the layout doesn't tip over.</p>
              <a href="#" style="display:inline-block;color:#0033A0;font-size:14px;font-weight:700;text-decoration:none;border-bottom:2px solid #0033A0;padding-bottom:2px;">Read more →</a>
            </td>
          </tr>
        </table>
      </td></tr>
      <tr><td style="padding-top:32px;color:#2F3847;font-size:12px;border-top:1px solid #CED7E0;margin-top:32px;">
        {{ agentName }}, {{ companyName }}
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 7. Receipt / Confirmation ----------
const receipt: EmailTemplateDefault = {
  key: "design.receipt",
  style: pageStyle("#E4E9ED"),
  category: "Structured",
  name: "Receipt / Confirmation",
  description:
    "Structured rows for confirmations, summaries, or any tabular content.",
  subject: "Confirmation #00000",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#E4E9ED;">
  <tr><td align="center" style="padding:40px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="560" style="max-width:560px;width:100%;background:#ffffff;border:1px solid #CED7E0;font-family:${FONT_SANS};">
      <tr><td style="padding:28px 32px;border-bottom:1px solid #CED7E0;">
        <div style="float:right;font-size:12px;color:#2F3847;">Reference</div>
        <div style="font-size:14px;font-weight:700;color:#0E1941;">{{ companyName }}</div>
        <div style="clear:both;"></div>
        <div style="margin-top:14px;font-size:13px;color:#2F3847;">Hi {{ firstName }}, here's a summary of what we just talked about.</div>
      </td></tr>
      <tr><td style="padding:8px 32px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="font-size:14px;color:#2F3847;">
          <tr><td style="padding:14px 0;border-bottom:1px solid #E4E9ED;width:40%;color:#2F3847;">Item</td><td style="padding:14px 0;border-bottom:1px solid #E4E9ED;text-align:right;color:#0E1941;font-weight:600;">Value</td></tr>
          <tr><td style="padding:14px 0;border-bottom:1px solid #E4E9ED;color:#2F3847;">Item</td><td style="padding:14px 0;border-bottom:1px solid #E4E9ED;text-align:right;color:#0E1941;font-weight:600;">Value</td></tr>
          <tr><td style="padding:14px 0;border-bottom:1px solid #E4E9ED;color:#2F3847;">Item</td><td style="padding:14px 0;border-bottom:1px solid #E4E9ED;text-align:right;color:#0E1941;font-weight:600;">Value</td></tr>
          <tr><td style="padding:18px 0 8px 0;color:#0E1941;font-weight:700;font-size:15px;">Total</td><td style="padding:18px 0 8px 0;text-align:right;color:#0E1941;font-weight:700;font-size:15px;">$0.00</td></tr>
        </table>
      </td></tr>
      <tr><td style="padding:24px 32px 32px 32px;border-top:1px solid #CED7E0;background:#E4E9ED;">
        <p style="margin:0 0 16px 0;font-size:13px;line-height:1.6;color:#2F3847;">If anything above doesn't match what we discussed, reply to this email or call {{ agentPhone }}.</p>
        <a href="#" style="display:inline-block;padding:10px 20px;background:#0E1941;color:#ffffff;font-size:13px;font-weight:600;text-decoration:none;border-radius:4px;">View full details</a>
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 8. Multi-Section Newsletter ----------
const multiSection: EmailTemplateDefault = {
  key: "design.multi-section",
  style: pageStyle("#E4E9ED"),
  category: "Structured",
  name: "Multi-Section Newsletter",
  description:
    "Header plus three stacked sections with horizontal dividers. For round-ups.",
  subject: "This month from {{ companyName }}",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#E4E9ED;">
  <tr><td align="center" style="padding:32px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;width:100%;background:#ffffff;font-family:${FONT_SANS};">
      <tr><td style="padding:36px 40px 24px 40px;text-align:center;border-bottom:3px solid #0E1941;">
        <div style="font-size:11px;letter-spacing:0.2em;text-transform:uppercase;color:#2F3847;font-weight:700;">Volume 01 · This month</div>
        <h1 style="margin:10px 0 0 0;font-size:30px;line-height:1.2;font-weight:800;color:#0E1941;letter-spacing:-0.01em;">{{ companyName }}</h1>
      </td></tr>
      <tr><td style="padding:32px 40px 16px 40px;">
        <div style="font-size:10px;letter-spacing:0.18em;text-transform:uppercase;color:#45BCE5;font-weight:700;margin-bottom:8px;">Section one</div>
        <h2 style="margin:0 0 12px 0;font-size:20px;line-height:1.3;font-weight:700;color:#0E1941;">A heading for the first story.</h2>
        <p style="margin:0;font-size:14px;line-height:1.65;color:#2F3847;">A short paragraph introducing the first item. Two or three sentences keeps the rhythm of the layout going.</p>
      </td></tr>
      <tr><td style="padding:0 40px;"><div style="height:1px;background:#CED7E0;"></div></td></tr>
      <tr><td style="padding:24px 40px 16px 40px;">
        <div style="font-size:10px;letter-spacing:0.18em;text-transform:uppercase;color:#F44025;font-weight:700;margin-bottom:8px;">Section two</div>
        <h2 style="margin:0 0 12px 0;font-size:20px;line-height:1.3;font-weight:700;color:#0E1941;">A heading for the second story.</h2>
        <p style="margin:0;font-size:14px;line-height:1.65;color:#2F3847;">Same shape as the section above. Different color accent gives the eye an easy way to scan.</p>
      </td></tr>
      <tr><td style="padding:0 40px;"><div style="height:1px;background:#CED7E0;"></div></td></tr>
      <tr><td style="padding:24px 40px 32px 40px;">
        <div style="font-size:10px;letter-spacing:0.18em;text-transform:uppercase;color:#45BCE5;font-weight:700;margin-bottom:8px;">Section three</div>
        <h2 style="margin:0 0 12px 0;font-size:20px;line-height:1.3;font-weight:700;color:#0E1941;">A heading for the third story.</h2>
        <p style="margin:0 0 18px 0;font-size:14px;line-height:1.65;color:#2F3847;">Wrap with the most actionable item if you can. The reader is most engaged here.</p>
        <a href="#" style="color:#0E1941;font-size:13px;font-weight:700;text-decoration:underline;">Read the full story →</a>
      </td></tr>
      <tr><td style="padding:24px 40px;background:#0E1941;color:#2F3847;font-size:12px;text-align:center;">
        {{ companyName }} · {{ agentPhone }} · {{ agentEmail }}
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 9. Stat Spotlight ----------
const statSpotlight: EmailTemplateDefault = {
  key: "design.stat-spotlight",
  style: pageStyle("#ffffff"),
  category: "Promo",
  name: "Stat Spotlight",
  description:
    "One giant number front and center. For metrics, savings, milestones.",
  subject: "The number that matters",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#ffffff;">
  <tr><td align="center" style="padding:48px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="560" style="max-width:560px;width:100%;font-family:${FONT_SANS};text-align:center;">
      <tr><td style="font-size:11px;letter-spacing:0.2em;text-transform:uppercase;color:#0033A0;font-weight:700;padding-bottom:24px;">
        {{ companyName }}
      </td></tr>
      <tr><td style="padding:24px 0;">
        <div style="font-size:108px;line-height:1;font-weight:900;color:#0E1941;letter-spacing:-0.04em;">00%</div>
        <div style="font-size:14px;color:#2F3847;margin-top:14px;letter-spacing:0.04em;">Replace with what the number means.</div>
      </td></tr>
      <tr><td style="padding:32px 40px 0 40px;">
        <p style="margin:0;font-size:16px;line-height:1.6;color:#2F3847;">Hi {{ firstName }}, use one to two sentences to set up the number and explain why it matters for them specifically.</p>
      </td></tr>
      <tr><td style="padding:32px 0;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center">
          <tr><td style="background:#0033A0;border-radius:6px;">
            <a href="#" style="display:inline-block;padding:14px 32px;color:#ffffff;font-size:15px;font-weight:600;text-decoration:none;">See how</a>
          </td></tr>
        </table>
      </td></tr>
      <tr><td style="padding-top:24px;color:#2F3847;font-size:12px;border-top:1px solid #CED7E0;">
        {{ agentName }}, {{ companyName }}
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 10. Three-Card Grid ----------
const threeCardGrid: EmailTemplateDefault = {
  key: "design.three-card-grid",
  style: pageStyle("#E4E9ED"),
  category: "Structured",
  name: "Three-Card Grid",
  description:
    "Three feature cards side-by-side. Great for options, benefits, or steps.",
  subject: "Three things worth knowing",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#E4E9ED;">
  <tr><td align="center" style="padding:40px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;width:100%;font-family:${FONT_SANS};">
      <tr><td style="padding-bottom:24px;text-align:center;">
        <h1 style="margin:0;font-size:26px;line-height:1.25;font-weight:800;color:#0E1941;">Three things, one quick read</h1>
        <p style="margin:10px 0 0 0;font-size:14px;color:#2F3847;">Hi {{ firstName }}, here's what's on our radar this week.</p>
      </td></tr>
      <tr><td>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>
            <td valign="top" width="33%" style="padding:6px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#ffffff;height:200px;">
                <tr><td style="padding:20px;">
                  <div style="width:36px;height:36px;background:#0033A0;border-radius:50%;margin-bottom:14px;"></div>
                  <h3 style="margin:0 0 8px 0;font-size:14px;font-weight:700;color:#0E1941;">First card</h3>
                  <p style="margin:0;font-size:12px;line-height:1.55;color:#2F3847;">One or two short sentences for the first feature.</p>
                </td></tr>
              </table>
            </td>
            <td valign="top" width="33%" style="padding:6px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#ffffff;height:200px;">
                <tr><td style="padding:20px;">
                  <div style="width:36px;height:36px;background:#45BCE5;border-radius:50%;margin-bottom:14px;"></div>
                  <h3 style="margin:0 0 8px 0;font-size:14px;font-weight:700;color:#0E1941;">Second card</h3>
                  <p style="margin:0;font-size:12px;line-height:1.55;color:#2F3847;">Same shape, different angle.</p>
                </td></tr>
              </table>
            </td>
            <td valign="top" width="33%" style="padding:6px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#ffffff;height:200px;">
                <tr><td style="padding:20px;">
                  <div style="width:36px;height:36px;background:#F44025;border-radius:50%;margin-bottom:14px;"></div>
                  <h3 style="margin:0 0 8px 0;font-size:14px;font-weight:700;color:#0E1941;">Third card</h3>
                  <p style="margin:0;font-size:12px;line-height:1.55;color:#2F3847;">Close strong on the last one.</p>
                </td></tr>
              </table>
            </td>
          </tr>
        </table>
      </td></tr>
      <tr><td style="padding-top:28px;text-align:center;">
        <a href="#" style="display:inline-block;padding:12px 26px;background:#0E1941;color:#ffffff;font-size:14px;font-weight:600;text-decoration:none;border-radius:6px;">View all</a>
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 11. CTA-Forward ----------
const ctaForward: EmailTemplateDefault = {
  key: "design.cta-forward",
  style: pageStyle("#ffffff"),
  category: "Promo",
  name: "CTA-Forward",
  description:
    "One headline, one giant button. Nothing else. For nudge emails.",
  subject: "Two seconds is all this takes",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#ffffff;">
  <tr><td align="center" style="padding:96px 24px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="480" style="max-width:480px;width:100%;font-family:${FONT_SANS};text-align:center;">
      <tr><td>
        <h1 style="margin:0 0 12px 0;font-size:32px;line-height:1.2;font-weight:800;color:#0E1941;letter-spacing:-0.02em;">One short headline.<br/>One clear action.</h1>
        <p style="margin:0 0 36px 0;font-size:15px;color:#2F3847;">Hi {{ firstName }}, say it in one line.</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center">
          <tr><td style="background:#0E1941;border-radius:10px;">
            <a href="#" style="display:inline-block;padding:18px 44px;color:#ffffff;font-size:16px;font-weight:700;text-decoration:none;">Do the thing</a>
          </td></tr>
        </table>
        <p style="margin:32px 0 0 0;font-size:12px;color:#2F3847;">{{ agentName }}, {{ companyName }}</p>
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 12. Pull Quote ----------
const pullQuote: EmailTemplateDefault = {
  key: "design.pull-quote",
  style: pageStyle("#E4E9ED"),
  category: "Editorial",
  name: "Pull Quote",
  description:
    "Big serif quote front and center. For testimonials, stories, or news.",
  subject: "A quote worth pausing on",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#E4E9ED;">
  <tr><td align="center" style="padding:64px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="560" style="max-width:560px;width:100%;font-family:${FONT_SANS};">
      <tr><td style="text-align:center;color:#0033A0;font-size:60px;line-height:1;font-family:${FONT_SERIF};font-weight:700;padding-bottom:8px;">“</td></tr>
      <tr><td style="text-align:center;padding:0 24px;">
        <p style="margin:0;font-family:${FONT_SERIF};font-size:24px;line-height:1.4;font-style:italic;color:#0E1941;font-weight:400;">A line worth lifting out and giving its own space. Short, true, and in the voice of a real person.</p>
      </td></tr>
      <tr><td style="text-align:center;padding:24px 0 0 0;">
        <div style="font-size:13px;font-weight:700;color:#0E1941;">First Last</div>
        <div style="font-size:12px;color:#0033A0;letter-spacing:0.06em;text-transform:uppercase;margin-top:4px;">Customer · 2024</div>
      </td></tr>
      <tr><td style="padding:40px 24px 0 24px;">
        <p style="margin:0;font-size:15px;line-height:1.65;color:#2F3847;text-align:center;">Hi {{ firstName }}, two short sentences of context underneath the quote. What's the story, why are we sharing it.</p>
      </td></tr>
      <tr><td style="padding:32px 0 0 0;text-align:center;">
        <a href="#" style="color:#0033A0;font-size:14px;font-weight:700;text-decoration:none;border-bottom:2px solid #0033A0;padding-bottom:2px;">Read the full story</a>
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 13. Dark Mode ----------
const darkMode: EmailTemplateDefault = {
  key: "design.dark-mode",
  style: pageStyle("#0E1941"),
  category: "Promo",
  name: "Dark Mode",
  description:
    "Dark background with red-orange accents. High-contrast and modern.",
  subject: "Something premium",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#0E1941;">
  <tr><td align="center" style="padding:48px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="560" style="max-width:560px;width:100%;background:#0E1941;border:1px solid #0E1941;font-family:${FONT_SANS};">
      <tr><td style="padding:36px 36px 0 36px;">
        <div style="font-size:11px;letter-spacing:0.2em;text-transform:uppercase;color:#F44025;font-weight:700;">{{ companyName }}</div>
        <h1 style="margin:18px 0 14px 0;font-size:28px;line-height:1.25;font-weight:800;color:#E4E9ED;">Headline in clean white,<br/>against the dark.</h1>
        <p style="margin:0;font-size:15px;line-height:1.7;color:#FFFFFF;">Hi {{ firstName }}, dark designs feel premium when the typography stays simple. Body copy in a soft gray, headlines in white, one accent color that does all the work.</p>
      </td></tr>
      <tr><td style="padding:28px 36px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0">
          <tr><td style="background:#F44025;border-radius:6px;">
            <a href="#" style="display:inline-block;padding:13px 28px;color:#0E1941;font-size:14px;font-weight:700;text-decoration:none;letter-spacing:0.02em;">Continue</a>
          </td></tr>
        </table>
      </td></tr>
      <tr><td style="padding:24px 36px 32px 36px;border-top:1px solid #0E1941;color:#45BCE5;font-size:12px;">
        {{ agentName }} · {{ agentPhone }}
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 14. Editorial Serif ----------
const editorialSerif: EmailTemplateDefault = {
  key: "design.editorial-serif",
  style: pageStyle("#E4E9ED"),
  category: "Editorial",
  name: "Editorial Serif",
  description:
    "Serif typography with a drop-cap opening. Reads like a published piece.",
  subject: "A piece worth your time",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#E4E9ED;">
  <tr><td align="center" style="padding:56px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="560" style="max-width:560px;width:100%;font-family:${FONT_SERIF};">
      <tr><td style="text-align:center;font-family:${FONT_SANS};font-size:11px;letter-spacing:0.22em;text-transform:uppercase;color:#0033A0;font-weight:700;padding-bottom:20px;">
        {{ companyName }} · Notes
      </td></tr>
      <tr><td style="text-align:center;padding-bottom:8px;">
        <h1 style="margin:0;font-size:38px;line-height:1.2;font-weight:700;color:#0E1941;letter-spacing:-0.01em;">A long-form headline,<br/>set in serif.</h1>
      </td></tr>
      <tr><td style="text-align:center;padding:0 0 28px 0;font-family:${FONT_SANS};font-size:12px;color:#2F3847;letter-spacing:0.04em;">
        By {{ agentName }} · 3 minute read
      </td></tr>
      <tr><td style="padding:0 16px;">
        <p style="margin:0 0 18px 0;font-size:17px;line-height:1.7;color:#2F3847;"><span style="float:left;font-size:54px;line-height:0.9;font-weight:700;color:#0033A0;padding:6px 8px 0 0;">H</span>i {{ firstName }}, the first paragraph wraps around an oversized drop-cap, which sets a deliberately slower pace than a marketing email. Use that pace.</p>
        <p style="margin:0 0 18px 0;font-size:17px;line-height:1.7;color:#2F3847;">The second paragraph keeps the same rhythm. Long-form serif emails are best when you have something thoughtful to say: a story, a perspective, a piece of news that deserves more than a bullet list.</p>
        <p style="margin:0 0 28px 0;font-size:17px;line-height:1.7;color:#2F3847;">Close with the takeaway. One sentence the reader can hold onto.</p>
      </td></tr>
      <tr><td style="padding:0 16px;text-align:center;">
        <a href="#" style="font-family:${FONT_SANS};color:#0033A0;font-size:13px;font-weight:700;text-decoration:none;letter-spacing:0.06em;text-transform:uppercase;border-bottom:1px solid #0033A0;padding-bottom:2px;">Continue reading</a>
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 15. Announcement Pill ----------
const announcementPill: EmailTemplateDefault = {
  key: "design.announcement-pill",
  style: pageStyle("#ffffff"),
  category: "Promo",
  name: "Announcement Pill",
  description:
    "Small colored pill tag, oversized headline, sparse body. For news drops.",
  subject: "Something new",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#ffffff;">
  <tr><td align="center" style="padding:80px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="560" style="max-width:560px;width:100%;font-family:${FONT_SANS};">
      <tr><td style="padding-bottom:24px;">
        <span style="display:inline-block;padding:6px 14px;background:#E4E9ED;color:#0033A0;font-size:11px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;border-radius:999px;">New</span>
      </td></tr>
      <tr><td>
        <h1 style="margin:0 0 20px 0;font-size:40px;line-height:1.15;font-weight:800;color:#0E1941;letter-spacing:-0.02em;">A headline that says it cleanly in one line.</h1>
        <p style="margin:0 0 32px 0;font-size:17px;line-height:1.6;color:#2F3847;">Hi {{ firstName }}, one paragraph of context. What it is, who it's for, why now.</p>
      </td></tr>
      <tr><td>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0">
          <tr>
            <td style="background:#0033A0;border-radius:6px;padding-right:0;">
              <a href="#" style="display:inline-block;padding:13px 26px;color:#ffffff;font-size:14px;font-weight:600;text-decoration:none;">See it</a>
            </td>
            <td style="padding-left:14px;">
              <a href="#" style="display:inline-block;padding:13px 0;color:#0E1941;font-size:14px;font-weight:600;text-decoration:none;">Maybe later →</a>
            </td>
          </tr>
        </table>
      </td></tr>
      <tr><td style="padding-top:48px;color:#2F3847;font-size:12px;">
        {{ agentName }}, {{ companyName }}
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 16. Comparison ----------
const comparison: EmailTemplateDefault = {
  key: "design.comparison",
  style: pageStyle("#FFFFFF"),
  category: "Comparison",
  name: "Side-by-side",
  description:
    "Two-column feature comparison with a highlighted recommendation. For upgrade and option emails.",
  subject: "Two coverage options to compare",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;">
  <tr><td align="center" style="padding:40px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;width:100%;font-family:${FONT_SANS};">
      <tr><td style="padding-bottom:24px;">
        <div style="font-size:11px;letter-spacing:0.18em;text-transform:uppercase;color:#0033A0;font-weight:700;">Coverage options</div>
        <h1 style="margin:10px 0 12px 0;font-size:26px;line-height:1.25;font-weight:800;color:#0E1941;">Compare side by side.</h1>
        <p style="margin:0;font-size:14px;color:#2F3847;line-height:1.6;">Hi {{ firstName }}, here's what your current coverage includes, and what's added if you upgrade.</p>
      </td></tr>
      <tr><td>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>
            <td width="50%" style="border:1px solid #CED7E0;padding:24px;vertical-align:top;">
              <div style="font-size:11px;letter-spacing:0.14em;text-transform:uppercase;color:#2F3847;font-weight:700;">Current</div>
              <div style="margin-top:6px;font-size:18px;font-weight:700;color:#0E1941;">$00 / mo</div>
              <div style="margin-top:18px;font-size:13px;line-height:1.7;color:#2F3847;">
                <div>+ Liability coverage</div>
                <div>+ Collision &amp; comprehensive</div>
                <div>+ Roadside assistance</div>
                <div style="color:#CED7E0;">No rental car coverage</div>
                <div style="color:#CED7E0;">No accident forgiveness</div>
              </div>
            </td>
            <td width="50%" style="border:2px solid #0033A0;background:#E4E9ED;padding:23px;vertical-align:top;">
              <div style="font-size:11px;letter-spacing:0.14em;text-transform:uppercase;color:#0033A0;font-weight:700;">Recommended</div>
              <div style="margin-top:6px;font-size:18px;font-weight:700;color:#0E1941;">$00 / mo</div>
              <div style="margin-top:18px;font-size:13px;line-height:1.7;color:#2F3847;">
                <div>+ Liability coverage</div>
                <div>+ Collision &amp; comprehensive</div>
                <div>+ Roadside assistance</div>
                <div style="color:#0033A0;font-weight:600;">+ Rental car coverage</div>
                <div style="color:#0033A0;font-weight:600;">+ Accident forgiveness</div>
              </div>
            </td>
          </tr>
        </table>
      </td></tr>
      <tr><td style="padding-top:28px;text-align:center;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center">
          <tr><td style="background:#0033A0;border-radius:6px;">
            <a href="#" style="display:inline-block;padding:14px 32px;color:#FFFFFF;font-size:15px;font-weight:600;text-decoration:none;">Talk to {{ agentName }}</a>
          </td></tr>
        </table>
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 17. Testimonial ----------
const testimonial: EmailTemplateDefault = {
  key: "design.testimonial",
  style: pageStyle("#E4E9ED"),
  category: "Social Proof",
  name: "Testimonial",
  description:
    "A short customer quote, photo, and outcome, followed by a short pitch. Conversion-focused.",
  subject: "How one customer saved $1,200",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#E4E9ED;">
  <tr><td align="center" style="padding:48px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="560" style="max-width:560px;width:100%;background:#FFFFFF;font-family:${FONT_SANS};">
      <tr><td style="padding:36px 36px 0 36px;text-align:center;">
        <div style="font-size:11px;letter-spacing:0.2em;text-transform:uppercase;color:#0033A0;font-weight:700;">Customer story</div>
      </td></tr>
      <tr><td style="padding:24px 36px 0 36px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>
            <td width="140" valign="middle" style="padding-right:20px;">
              <div style="width:120px;height:120px;background:#45BCE5;border-radius:60px;text-align:center;vertical-align:middle;color:#FFFFFF;font-size:28px;font-weight:800;line-height:120px;">FL</div>
            </td>
            <td valign="middle">
              <p style="margin:0;font-family:${FONT_SERIF};font-size:20px;line-height:1.4;font-style:italic;color:#0E1941;">"Switching saved me $1,200 a year, and I didn't have to lift a finger."</p>
            </td>
          </tr>
        </table>
      </td></tr>
      <tr><td style="padding:18px 36px 0 36px;">
        <div style="font-size:13px;font-weight:700;color:#0E1941;">First Last</div>
        <div style="font-size:12px;color:#2F3847;margin-top:2px;">Customer since 2022 · Saved $1,247/yr</div>
      </td></tr>
      <tr><td style="padding:24px 36px 36px 36px;color:#2F3847;font-size:14px;line-height:1.6;">
        <p style="margin:0 0 22px 0;">Hi {{ firstName }}, one of our customers had a result we wanted to share with you. We think the same kind of savings is on the table for your situation too.</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0">
          <tr><td style="background:#0033A0;border-radius:6px;">
            <a href="#" style="display:inline-block;padding:12px 26px;color:#FFFFFF;font-size:14px;font-weight:600;text-decoration:none;">See if you can save</a>
          </td></tr>
        </table>
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 18. Action List ----------
const actionList: EmailTemplateDefault = {
  key: "design.action-list",
  style: pageStyle("#FFFFFF"),
  category: "Onboarding",
  name: "Action List",
  description:
    "Three numbered, stacked option boxes, each with its own mini-CTA. For onboarding sequences.",
  subject: "Three quick things to set up",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;">
  <tr><td align="center" style="padding:40px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;width:100%;font-family:${FONT_SANS};">
      <tr><td style="padding-bottom:24px;">
        <h1 style="margin:0;font-size:26px;line-height:1.25;font-weight:800;color:#0E1941;">Three things you can do next.</h1>
        <p style="margin:8px 0 0 0;font-size:14px;color:#2F3847;line-height:1.6;">Hi {{ firstName }}, short list, no rush. Pick one to start.</p>
      </td></tr>
      <tr><td>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border:1px solid #CED7E0;">
          <tr>
            <td width="48" valign="top" style="background:#0033A0;color:#FFFFFF;font-size:20px;font-weight:800;text-align:center;padding:18px 0;">1</td>
            <td style="padding:18px 20px;">
              <div style="font-size:15px;font-weight:700;color:#0E1941;">Set up your account</div>
              <div style="font-size:13px;color:#2F3847;margin-top:4px;line-height:1.55;">Five minutes. Adds your billing details and contact preferences.</div>
              <a href="#" style="display:inline-block;margin-top:10px;font-size:13px;color:#0033A0;font-weight:600;text-decoration:none;">Start →</a>
            </td>
          </tr>
        </table>
      </td></tr>
      <tr><td style="padding-top:12px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border:1px solid #CED7E0;">
          <tr>
            <td width="48" valign="top" style="background:#0033A0;color:#FFFFFF;font-size:20px;font-weight:800;text-align:center;padding:18px 0;">2</td>
            <td style="padding:18px 20px;">
              <div style="font-size:15px;font-weight:700;color:#0E1941;">Add your other vehicles</div>
              <div style="font-size:13px;color:#2F3847;margin-top:4px;line-height:1.55;">Bundle discounts kick in at vehicle two. Most customers save 12-18%.</div>
              <a href="#" style="display:inline-block;margin-top:10px;font-size:13px;color:#0033A0;font-weight:600;text-decoration:none;">Add a vehicle →</a>
            </td>
          </tr>
        </table>
      </td></tr>
      <tr><td style="padding-top:12px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border:1px solid #CED7E0;">
          <tr>
            <td width="48" valign="top" style="background:#0033A0;color:#FFFFFF;font-size:20px;font-weight:800;text-align:center;padding:18px 0;">3</td>
            <td style="padding:18px 20px;">
              <div style="font-size:15px;font-weight:700;color:#0E1941;">Get the mobile app</div>
              <div style="font-size:13px;color:#2F3847;margin-top:4px;line-height:1.55;">Roadside, claims, and your digital ID card live there. Free.</div>
              <a href="#" style="display:inline-block;margin-top:10px;font-size:13px;color:#0033A0;font-weight:600;text-decoration:none;">Download →</a>
            </td>
          </tr>
        </table>
      </td></tr>
      <tr><td style="padding-top:28px;color:#2F3847;font-size:12px;line-height:1.5;">
        {{ agentName }}, {{ companyName }}
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 19. Agent Note ----------
const agentNote: EmailTemplateDefault = {
  key: "design.agent-note",
  style: pageStyle("#E4E9ED"),
  category: "Personal",
  name: "Agent Note",
  description:
    "A personal letter that visibly comes from a specific agent. Avatar, identity card, handwritten signature.",
  subject: "Just checking in",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#E4E9ED;">
  <tr><td align="center" style="padding:48px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="560" style="max-width:560px;width:100%;background:#FFFFFF;font-family:${FONT_SANS};">
      <tr><td style="padding:32px 36px 0 36px;text-align:center;">
        <div style="font-family:${FONT_SERIF};font-size:14px;font-style:italic;color:#0033A0;letter-spacing:0.04em;">a personal message</div>
      </td></tr>
      <tr><td style="padding:24px 36px 0 36px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#0033A0;">
          <tr>
            <td width="106" valign="top" style="padding:18px 0 18px 18px;">
              <div style="width:80px;height:80px;background:#FFFFFF;border-radius:8px;text-align:center;vertical-align:middle;color:#0033A0;font-size:28px;font-weight:800;line-height:80px;letter-spacing:-0.02em;">AN</div>
            </td>
            <td valign="middle" style="padding:18px;">
              <div style="font-size:19px;font-weight:800;color:#FFFFFF;letter-spacing:-0.01em;">{{ agentName }}</div>
              <div style="font-size:11px;color:#45BCE5;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;margin-top:4px;">Your dedicated agent</div>
              <div style="font-size:13px;color:#FFFFFF;margin-top:10px;line-height:1.55;">
                {{ companyName }}<br/>
                <a href="tel:{{ agentPhone }}" style="color:#45BCE5;text-decoration:none;">{{ agentPhone }}</a> · <a href="mailto:{{ agentEmail }}" style="color:#45BCE5;text-decoration:none;">{{ agentEmail }}</a>
              </div>
            </td>
          </tr>
        </table>
      </td></tr>
      <tr><td style="padding:32px 36px 0 36px;color:#2F3847;font-size:16px;line-height:1.75;">
        <p style="margin:0 0 18px 0;">Hi {{ firstName }},</p>
        <p style="margin:0 0 18px 0;">Just wanted to check in. I've been your agent for a little while now and I wanted to make sure things are working the way you expected, and to give you a chance to ask anything you've been meaning to.</p>
        <p style="margin:0 0 18px 0;">If there's anything on your mind (a coverage question, a billing thing, a life change that might affect your policy) reply directly to this email or text me at {{ agentPhone }}. Otherwise, no need to do anything.</p>
        <p style="margin:0 0 8px 0;">Talk soon,</p>
      </td></tr>
      <tr><td style="padding:8px 36px 24px 36px;">
        <div style="font-family:${FONT_SERIF};font-size:38px;font-style:italic;color:#0033A0;font-weight:400;letter-spacing:-0.01em;line-height:1.1;">{{ agentName }}</div>
      </td></tr>
      <tr><td style="padding:0 36px 32px 36px;border-top:1px solid #CED7E0;padding-top:18px;color:#2F3847;font-size:12px;line-height:1.6;">
        Reach me Mon-Fri, 9-5. I read every reply myself. No auto-responders, no call trees.
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 20. Savings Spotlight ----------
const savingsSpotlight: EmailTemplateDefault = {
  key: "design.savings-spotlight",
  style: pageStyle("#FFFFFF"),
  category: "Promo",
  name: "Savings Spotlight",
  description:
    "Big dollar number with a discount-by-discount breakdown table. For loyalty and value emails.",
  subject: "How much you saved this year",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;">
  <tr><td align="center" style="padding:48px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="560" style="max-width:560px;width:100%;font-family:${FONT_SANS};">
      <tr><td style="text-align:center;padding-bottom:8px;">
        <div style="font-size:11px;letter-spacing:0.2em;text-transform:uppercase;color:#0033A0;font-weight:700;">Your yearly savings</div>
      </td></tr>
      <tr><td style="text-align:center;padding-bottom:8px;">
        <div style="font-size:72px;line-height:1;font-weight:900;color:#0E1941;letter-spacing:-0.04em;">$1,247</div>
      </td></tr>
      <tr><td style="text-align:center;padding-bottom:32px;">
        <div style="font-size:14px;color:#2F3847;letter-spacing:0.02em;">saved this year vs your previous policy</div>
      </td></tr>
      <tr><td style="background:#E4E9ED;padding:24px 28px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="font-size:13px;color:#2F3847;line-height:1.6;">
          <tr><td style="padding:6px 0;">Multi-line discount</td><td align="right" style="padding:6px 0;font-weight:700;color:#0E1941;">$420</td></tr>
          <tr><td style="padding:6px 0;border-top:1px solid #CED7E0;">Safe driver discount</td><td align="right" style="padding:6px 0;border-top:1px solid #CED7E0;font-weight:700;color:#0E1941;">$390</td></tr>
          <tr><td style="padding:6px 0;border-top:1px solid #CED7E0;">Paperless billing</td><td align="right" style="padding:6px 0;border-top:1px solid #CED7E0;font-weight:700;color:#0E1941;">$240</td></tr>
          <tr><td style="padding:6px 0;border-top:1px solid #CED7E0;">Loyalty (3+ years)</td><td align="right" style="padding:6px 0;border-top:1px solid #CED7E0;font-weight:700;color:#0E1941;">$197</td></tr>
        </table>
      </td></tr>
      <tr><td style="padding:28px 0 0 0;color:#2F3847;font-size:15px;line-height:1.65;">
        <p style="margin:0 0 22px 0;">Hi {{ firstName }}, this is what staying with us looks like in real dollars. There are a couple more discounts you might qualify for too.</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0">
          <tr><td style="background:#0033A0;border-radius:6px;">
            <a href="#" style="display:inline-block;padding:13px 26px;color:#FFFFFF;font-size:14px;font-weight:600;text-decoration:none;">See what else you qualify for</a>
          </td></tr>
        </table>
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

// ---------- 21. Event Invite ----------
const eventInvite: EmailTemplateDefault = {
  key: "design.event-invite",
  style: pageStyle("#E4E9ED"),
  category: "Invite",
  name: "Event Invite",
  description:
    "A three-block details strip (when/where/RSVP) with an invite headline. For open houses and community events.",
  subject: "You're invited",
  text: `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#E4E9ED;">
  <tr><td align="center" style="padding:48px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="540" style="max-width:540px;width:100%;background:#FFFFFF;font-family:${FONT_SANS};">
      <tr><td style="padding:32px 32px 0 32px;text-align:center;">
        <div style="font-size:11px;letter-spacing:0.2em;text-transform:uppercase;color:#F44025;font-weight:700;">You're invited</div>
        <h1 style="margin:14px 0 0 0;font-size:28px;line-height:1.2;font-weight:800;color:#0E1941;letter-spacing:-0.01em;">Coffee &amp; Coverage</h1>
        <div style="margin-top:8px;font-size:13px;color:#2F3847;">An informal Q&amp;A with the {{ companyName }} team</div>
      </td></tr>
      <tr><td style="padding:24px 32px 0 32px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#E4E9ED;">
          <tr>
            <td width="33%" style="padding:18px 12px;text-align:center;border-right:1px solid #CED7E0;">
              <div style="font-size:10px;letter-spacing:0.18em;text-transform:uppercase;color:#0033A0;font-weight:700;">When</div>
              <div style="margin-top:6px;font-size:14px;font-weight:700;color:#0E1941;">Sat, Jun 21</div>
              <div style="margin-top:2px;font-size:12px;color:#2F3847;">9:00 - 11:00 AM</div>
            </td>
            <td width="33%" style="padding:18px 12px;text-align:center;border-right:1px solid #CED7E0;">
              <div style="font-size:10px;letter-spacing:0.18em;text-transform:uppercase;color:#0033A0;font-weight:700;">Where</div>
              <div style="margin-top:6px;font-size:14px;font-weight:700;color:#0E1941;">Office</div>
              <div style="margin-top:2px;font-size:12px;color:#2F3847;">123 Main St</div>
            </td>
            <td width="33%" style="padding:18px 12px;text-align:center;">
              <div style="font-size:10px;letter-spacing:0.18em;text-transform:uppercase;color:#0033A0;font-weight:700;">RSVP</div>
              <div style="margin-top:6px;font-size:14px;font-weight:700;color:#0E1941;">By Jun 18</div>
              <div style="margin-top:2px;font-size:12px;color:#2F3847;">Light brunch</div>
            </td>
          </tr>
        </table>
      </td></tr>
      <tr><td style="padding:28px 32px 0 32px;color:#2F3847;font-size:14px;line-height:1.65;">
        <p style="margin:0 0 22px 0;">Hi {{ firstName }}, bring whatever insurance questions have been sitting on your mind. No agenda, just coffee and answers.</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0">
          <tr><td style="background:#0033A0;border-radius:6px;">
            <a href="#" style="display:inline-block;padding:13px 26px;color:#FFFFFF;font-size:14px;font-weight:600;text-decoration:none;">I'll be there</a>
          </td></tr>
        </table>
      </td></tr>
      <tr><td style="padding:24px 32px 32px 32px;color:#2F3847;font-size:12px;line-height:1.5;">
        {{ agentName }}, {{ companyName }} · {{ agentPhone }}
      </td></tr>
    </table>
  </td></tr>
</table>`.trim(),
};

export const EMAIL_DEFAULTS: EmailTemplateDefault[] = [
  plainLetter,
  indigoBanner,
  centeredCard,
  boldHero,
  imageHero,
  twoColumnFeature,
  receipt,
  multiSection,
  statSpotlight,
  threeCardGrid,
  ctaForward,
  pullQuote,
  darkMode,
  editorialSerif,
  announcementPill,
  comparison,
  testimonial,
  actionList,
  agentNote,
  savingsSpotlight,
  eventInvite,
];

export const EMAIL_DEFAULT_CATEGORIES = Array.from(
  new Set(EMAIL_DEFAULTS.map((d) => d.category))
);
